import React, { HTMLAttributes, useEffect, useMemo, useState } from 'react'
import { IpdObject } from '../../entities/IpdObject/IpdObject'
import { Select, SelectOption } from '../../shared/ui/Select'
import {
  IpdObjectInfo,
  IpdObjectPrefix,
  ErrorMessage
} from './IpdObjectSelector.styles'
import { useIpdObjects } from '../../shared/hooks/api'

interface IpdObjectSelectorProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  livingComplexId: string
  selectedId?: string | null
  onChange: (ipdObject: IpdObject) => void
  disabled?: boolean
}

export const IpdObjectSelector: React.FC<IpdObjectSelectorProps> = ({
  selectedId,
  onChange,
  livingComplexId,
  disabled,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchValue)
    }, 400)

    return () => clearTimeout(timeoutId)
  }, [searchValue])

  useEffect(() => {
    setSearchValue('')
    setDebouncedSearch('')
  }, [livingComplexId])

  const {
    data: ipdObjects,
    isLoading,
    error
  } = useIpdObjects({
    livingComplexId,
    name: debouncedSearch || undefined
  })

  const selectOptions: SelectOption[] = useMemo(
    () =>
      ipdObjects?.map(ipdObject => ({
        value: ipdObject.id,
        label: ipdObject.name
      })) || [],
    [ipdObjects]
  )

  const handleIpdObjectChange = (ipdObjectId: string) => {
    const ipdObject = ipdObjects?.find(object => object.id === ipdObjectId)
    if (ipdObject) {
      onChange(ipdObject)
    }
  }

  return (
    <div {...props}>
      <IpdObjectInfo>
        <IpdObjectPrefix>ИПД объект:</IpdObjectPrefix>
        <Select
          options={selectOptions}
          value={selectedId ?? ''}
          onChange={handleIpdObjectChange}
          placeholder="Выберите ИПД-объект"
          disabled={disabled || isLoading}
          searchValue={searchValue}
          searchPlaceholder="Поиск ОИПД..."
          onSearchChange={setSearchValue}
          isLoading={isLoading}
        />
      </IpdObjectInfo>
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
    </div>
  )
}
